import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useCITranslate } from '../../i18n/customerIdentity';
import { useCiAdminBasePath } from '../../contexts/CiAdminAuthContext';
import CiSiteFilterBar from '../../components/CustomerIdentity/CiSiteFilterBar';
import {
  CIConfirmPopup,
  CIDrawer,
  CIInfoBar,
} from '../../components/CustomerIdentity/CustomerIdentityShared';
import CustomerForm from '../../components/CustomerIdentity/CustomerForm';
import CustomerAddressesTab from '../../components/CustomerIdentity/CustomerAddressesTab';
import CustomerPaymentsTab from '../../components/CustomerIdentity/CustomerPaymentsTab';
import CustomerUsersTab from '../../components/CustomerIdentity/CustomerUsersTab';
import EntitySettingsPanel from '../../components/CustomerIdentity/EntitySettingsPanel';
import {
  deleteCustomer,
  getCustomer,
  updateCustomer,
} from '../../services/customerIdentityApi';
import { reindexCustomerDirectoryRows } from '../../services/customerImportApi';
import { resolveCiAssetUrl } from '../../utils/ciAssets';

const TAB_IDS = ['users', 'addresses', 'payments'];

const errorMessage = (err) => ({ kind: 'danger', error: err });
const successMessage = (i18nKey) => ({ kind: 'success', i18nKey });

export default function CustomerDetail() {
  const t = useCITranslate();
  const { customerId } = useParams();
  const navigate = useNavigate();
  const basePath = useCiAdminBasePath();
  const customersPath = `${basePath}/customers`;
  const [searchParams, setSearchParams] = useSearchParams();

  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pageMessage, setPageMessage] = useState(null);
  const [editDrawerMessage, setEditDrawerMessage] = useState(null);
  const [editing, setEditing] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(false);
  const settingsRef = useRef(null);

  const rawTab = searchParams.get('tab');
  const activeTab = useMemo(
    () => (TAB_IDS.includes(rawTab) ? rawTab : 'users'),
    [rawTab],
  );

  const setTab = (tabId) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', tabId);
        return next;
      },
      { replace: true },
    );
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const c = await getCustomer(customerId);
      setCustomer(c);
    } catch (err) {
      setPageMessage({
        kind: 'danger',
        i18nKey: 'customer.couldNotLoadCustomer',
        error: err,
      });
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!editing) setEditDrawerMessage(null);
  }, [editing]);

  const handleUpdate = async (values) => {
    setBusy(true);
    setEditDrawerMessage(null);
    try {
      const updated = await updateCustomer(customerId, values);
      setCustomer(updated);
      try {
        await settingsRef.current?.flush();
      } catch (settingsErr) {
        setEditDrawerMessage(errorMessage(settingsErr));
        return;
      }
      // Real-time reindex so the OrderX typeahead reflects this edit
      // immediately. Fire-and-forget -- never throws, never blocks the save.
      reindexCustomerDirectoryRows('customer', [customerId]);
      setEditing(false);
      setPageMessage(successMessage('customer.customerUpdated'));
    } catch (err) {
      // Keep the drawer open and show the error inline. Page-level info bar
      // is reserved for outcomes the user can see only after the drawer
      // closes (e.g. successes).
      setEditDrawerMessage(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await deleteCustomer(customerId);
      navigate(customersPath);
    } catch (err) {
      setPageMessage(errorMessage(err));
      setBusy(false);
    }
  };

  const headerLogoSrc = customer
    ? resolveCiAssetUrl(
        customer.metadata?.customer_logo_url || customer.metadata?.logo_url || '',
      )
    : '';

  if (loading || !customer) {
    return (
      <div className="d-flex flex-column h-100">
        <CIInfoBar message={pageMessage} onDismiss={() => setPageMessage(null)} />
        <CiSiteFilterBar compact />
        <div className="p-3 text-muted">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column h-100">
      <CIInfoBar message={pageMessage} onDismiss={() => setPageMessage(null)} />
      <CiSiteFilterBar compact />
      <div
        className="bg-white px-3 py-2 border-bottom"
        style={{ position: 'sticky', top: 0, zIndex: 10 }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-link p-0 me-2"
              onClick={() => navigate(customersPath)}
            >
              <i className="bi bi-arrow-left" /> {t('customer.backToCustomers')}
            </button>
            {headerLogoSrc ? (
              <img
                src={headerLogoSrc}
                alt=""
                className="rounded border bg-white flex-shrink-0"
                style={{ width: 40, height: 40, objectFit: 'contain' }}
              />
            ) : null}
            <h2 className="h6 m-0 d-inline-block fw-semibold">{customer.name}</h2>
            <span className="badge bg-info ms-2">
              {t(`customer.accountTypes.${customer.account_type}`) || customer.account_type}
            </span>
            {customer.is_2fa_required ? (
              <span className="badge bg-warning ms-1">
                {t('customer.requires2fa')}
              </span>
            ) : null}
          </div>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setEditing(true)}
            >
              {t('common.edit')}
            </button>
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={() => setPendingDelete(true)}
            >
              {t('common.delete')}
            </button>
          </div>
        </div>
        <ul className="nav nav-tabs mt-2 ci-customer-detail-tabs" role="tablist">
          {TAB_IDS.map((tabId) => (
            <li className="nav-item" key={tabId}>
              <button
                type="button"
                className={`nav-link ${activeTab === tabId ? 'active' : ''}`}
                onClick={() => setTab(tabId)}
              >
                {t(`customer.tabs.${tabId}`)}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex-fill p-2 p-md-3 overflow-auto">
        <div className="container-fluid">
          {activeTab === 'users' ? (
            <CustomerUsersTab customer={customer} setMessage={setPageMessage} />
          ) : null}
          {activeTab === 'addresses' ? (
            <CustomerAddressesTab customer={customer} setMessage={setPageMessage} />
          ) : null}
          {activeTab === 'payments' ? (
            <CustomerPaymentsTab customer={customer} setMessage={setPageMessage} />
          ) : null}
        </div>
      </div>

      <CIDrawer
        open={editing}
        title={t('customer.editCustomer')}
        onClose={() => setEditing(false)}
      >
        <CustomerForm
          customer={customer}
          onSubmit={handleUpdate}
          onCancel={() => setEditing(false)}
          busy={busy}
          formMessage={editDrawerMessage}
          onDismissMessage={() => setEditDrawerMessage(null)}
          onLogoUpdated={(c) => setCustomer(c)}
        >
          <EntitySettingsPanel
            ref={settingsRef}
            scope="customer"
            scopeId={customer.id}
            contextSiteId={customer.site_id}
            contextCustomerId={customer.id}
            defaultExpanded
            onError={(err) => setEditDrawerMessage(errorMessage(err))}
          />
        </CustomerForm>
      </CIDrawer>

      <CIConfirmPopup
        open={pendingDelete}
        title={t('customer.deleteConfirmTitle')}
        message={t('customer.deleteConfirm', { name: customer.name })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        danger
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(false)}
      />
    </div>
  );
}
